import {
  AiIcon,
  Alert,
  AlertTitle,
  Button,
  DateTimePicker,
  Dialog,
  Stack,
  TextField,
} from '@all-manager/design-system-v2';
import { Controller } from 'react-hook-form';
import DescriptionField from '../../../../components/modals/components/DescriptionField';
import DriveFileField from '../../../documents/external-components/DriveFileField/DriveFileField';
import { DisplayedMeeting } from '../../types';
import { useCreateUpdateMeetingForm } from './useCreateUpdateMeetingForm';

type Props = {
  onClose: () => void;
  meeting?: DisplayedMeeting;
};

export function CreateUpdateMeetingModal(props: Props) {
  const hook = useCreateUpdateMeetingForm(props.onClose, props.meeting);

  return (
    <Dialog
      open
      onClose={props.onClose}
      onSubmit={hook.onSubmit}
      maxWidth="sm"
      fullWidth
      title={props.meeting ? 'Modifier la réunion' : 'Nouvelle réunion'}
      closeLabel="Fermer"
      actions={{
        primaryAction: {
          label: props.meeting ? 'Enregistrer' : 'Créer',
          type: 'submit',
          loading: hook.isPending,
        },
        secondaryAction: {
          label: 'Annuler',
          onClick: props.onClose,
          disabled: hook.isPending,
        },
      }}
    >
      <Stack spacing={3}>
        {hook.serverError ? <Alert severity="error">{hook.serverError}</Alert> : null}

        <Controller
          name="title"
          control={hook.form.control}
          render={({ field, fieldState }) => (
            <TextField
              name={field.name}
              inputRef={field.ref}
              onBlur={field.onBlur}
              onChange={field.onChange}
              value={field.value}
              label="Titre"
              fullWidth
              required
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <Controller
          name="start_time"
          control={hook.form.control}
          render={({ field }) => (
            <DateTimePicker
              label="Date et heure"
              value={new Date(field.value)}
              onChange={hook.handleStartTimeChange}
              inputRef={field.ref}
              required
              fullWidth
            />
          )}
        />

        <Controller
          name="description"
          control={hook.form.control}
          render={({ field }) => (
            <DescriptionField
              value={field.value}
              onChange={field.onChange}
              label={props.meeting ? 'Notes et résumé' : 'Ordre du jour'}
              action={props.meeting ? undefined : (
                <Button
                  size="small"
                  variant="text"
                  color="ai"
                  startIcon={<AiIcon fontSize="small" />}
                  onClick={hook.handleGenerateAgenda}
                  loading={hook.isGeneratingAgenda}
                  disabled={hook.isPending}
                >
                  Générer l'ordre du jour
                </Button>
              )}
            />
          )}
        />

        {!props.meeting && hook.isDriveConnected ? (
          <Alert severity="info">
            <AlertTitle>Un Google Docs sera créé</AlertTitle>
            Le contenu sera enregistré dans le dossier « Réunions » de votre Google Drive :
            vous pourrez le partager à des personnes qui n'ont pas accès à l'application et y travailler hors ligne.
          </Alert>
        ) : null}

        {props.meeting ? (
          <DriveFileField
            label="Le compte rendu"
            value={hook.document}
            onChange={hook.setDocument}
          />
        ) : null}
      </Stack>
    </Dialog>
  );
}
